import type { SurveyPageDefinition } from '@engage/types';
import { Icon, ICONS } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';

/**
 * Left-rail list of survey pages. Page 1 is always the entry point; branching
 * rules can route forward or to END. Selecting a page scopes the palette,
 * canvas, and the Page settings tab to it.
 */
export function PageRail(props: {
  pages: SurveyPageDefinition[];
  selectedPageId: string | null;
  onSelect: (pageId: string) => void;
  onAdd: () => void;
  onMove: (pageId: string, direction: 'up' | 'down') => void;
  onRemove: (pageId: string) => void;
}) {
  return (
    <div style={{ padding: '16px 14px 6px', fontFamily: FORM_UI_FONT, borderBottom: `1px solid ${C.line}` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, letterSpacing: 0.4 }}>PAGES</div>
        <button
          type="button"
          onClick={props.onAdd}
          title="Add page"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: `1px solid ${C.teal}`,
            background: '#fff',
            color: C.tealDark,
            borderRadius: 7,
            padding: '3px 8px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: FORM_UI_FONT,
          }}
        >
          <Icon d={ICONS.plus} size={12} color={C.tealDark} sw={2.6} />
          Page
        </button>
      </div>

      {props.pages.length === 0 ? (
        <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5, padding: '6px 2px 12px' }}>
          No pages yet: add the first page to begin.
        </div>
      ) : (
        props.pages.map((page, i) => {
          const active = page.id === props.selectedPageId;
          return (
            <div
              key={page.id}
              onClick={() => props.onSelect(page.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 9px',
                marginBottom: 5,
                borderRadius: 8,
                cursor: 'pointer',
                border: `1px solid ${active ? C.indigo : C.line}`,
                background: active ? C.candidateBg : '#fff',
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  borderRadius: 6,
                  background: active ? C.indigo : C.bg,
                  color: active ? '#fff' : C.sub,
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? C.indigo : C.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {page.label}
                {page.branching_rules.length > 0 && (
                  <span title="Has branching rules" style={{ marginLeft: 5, color: C.tealDark }}>
                    ⑂
                  </span>
                )}
              </span>
              <RailBtn
                disabled={i === 0}
                title="Move up"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onMove(page.id, 'up');
                }}
              >
                ↑
              </RailBtn>
              <RailBtn
                disabled={i === props.pages.length - 1}
                title="Move down"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onMove(page.id, 'down');
                }}
              >
                ↓
              </RailBtn>
              <RailBtn
                title="Remove page"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRemove(page.id);
                }}
              >
                ×
              </RailBtn>
            </div>
          );
        })
      )}
    </div>
  );
}

function RailBtn(props: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  danger?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      style={{
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: 5,
        border: 'none',
        background: 'transparent',
        color: props.disabled ? C.line : props.danger ? C.danger : C.faint,
        fontSize: 13,
        lineHeight: 1,
        cursor: props.disabled ? 'default' : 'pointer',
        fontFamily: FORM_UI_FONT,
      }}
    >
      {props.children}
    </button>
  );
}
